import {
  ActivitySubject,
  LearningFormatPreference,
} from "@outschool/business-rules";
import {
  IconDefinition,
  faBook,
  faCalculator,
  faChalkboard,
  faCode,
  faFlask,
  faGlobe,
  faGlobeRegular,
  faLanguage,
  faLightbulb,
  faMicroscope,
  faMusic,
  faPalette,
  faPuzzlePiece,
  faSchool,
  faStaffSnake,
  faTeddyBear,
} from "@outschool/icons";
import uniq from "lodash/uniq";

import { AvailableTime } from "../AvailableTimesInput/types";
import { TIMES_OF_DAY } from "../AvailableTimesInput/utils";

export { DEFAULT_PROFILE_IMAGE as DEFAULT_LEADER_PHOTO_IN_ACTIVITY_PREVIEW } from "@outschool/filestack-urls";

export enum OnboardingSourceParam {
  SaveActivity = "savedActivityUid",
  SaveSearch = "savedSearch",
  RequestTime = "requestTime",
  MessageTeacher = "messageTeacher",
  BuySubscription = "buySubscription",
  RedeemGiftCard = "redeemGiftCard",
  FollowTeacher = "followTeacher",
  RequestClass = "requestClass",
  Variant = "variant",
}

export const SOURCE_MAP = {
  [OnboardingSourceParam.SaveActivity]: "save_activity",
  [OnboardingSourceParam.SaveSearch]: "save_search",
  [OnboardingSourceParam.RequestTime]: "request_time",
  [OnboardingSourceParam.MessageTeacher]: "message_teacher",
  [OnboardingSourceParam.BuySubscription]: "buy_subscription",
  [OnboardingSourceParam.RedeemGiftCard]: "redeem_gift_card",
  [OnboardingSourceParam.FollowTeacher]: "follow_teacher",
  [OnboardingSourceParam.RequestClass]: "request_class",
  [OnboardingSourceParam.Variant]: "variant",
} as const;

export enum STEP {
  LearnerIdStep = "LearnerIdStep",
  ClassPreferencesStep = "ClassPreferencesStep",
  LearningNeedsStep = "LearningNeedsStep",
  AvailabilityStep = "AvailabilityStep",
  SignupStep = "SignupStep",
  FundingProgramStep = "FundingProgramStep",
  FirstMonthFreeStep = "FirstMonthFreeStep",
}

export enum Question {
  LearnerName = "learnerName",
  BirthMonth = "birthMonth",
  BirthYear = "birthYear",
  SchoolingApproach = "schoolingApproach",
  Subjects = "subjects",
  LearningFormats = "learningFormats",
  ClassSizes = "classSizes",
  LearningNeeds = "learningNeeds",
  AvailableTimes = "availableTimes",
}

export type QuestionAnswerType =
  LearnerAttributesType[keyof LearnerAttributesType];

export type LearnerAttributesType = {
  learnerName?: string;
  birthMonth?: number;
  birthYear?: number;
  schoolingApproach?: SchoolingApproachType;
  subjects?: ActivitySubject[];
  learningFormats?: LearningFormatPreference[];
  classSizes?: ClassSizeLearnerAttribute[];
  learningNeeds?: LearningNeedsType[];
  availableTimes?: AvailableTime[];
};

/**
 * Type guard to check if learner attributes contain all required fields for backend learner creation.
 * Backend requires: learnerName, birthMonth, birthYear, and schoolingApproach
 */
export type CompleteLearnerAttributes = LearnerAttributesType & {
  learnerName: string;
  birthMonth: number;
  birthYear: number;
  schoolingApproach: SchoolingApproachType;
};

/**
 * Type guard to validate if learner attributes are complete enough to create a learner.
 * Returns attributes with required fields typed as non-null if validation passes.
 */
export const isCompleteLearnerAttributes = (
  attrs: LearnerAttributesType,
): attrs is CompleteLearnerAttributes => {
  return Boolean(
    attrs.learnerName &&
      typeof attrs.birthMonth === "number" &&
      typeof attrs.birthYear === "number" &&
      attrs.schoolingApproach,
  );
};

type SchoolingApproachKeyType = keyof typeof SchoolingApproach;
export type SchoolingApproachType =
  (typeof SchoolingApproach)[SchoolingApproachKeyType];

export enum SchoolingApproach {
  OnCampus = "on_campus",
  AtHome = "at_home",
  Hybrid = "hybrid",
}

export const SCHOOLING_APPROACH_SELECTIONS = [
  {
    value: SchoolingApproach.OnCampus,
    label: "On campus",
    icon: faSchool,
    subtitle: "Public, charter, private",
  },
  {
    value: SchoolingApproach.AtHome,
    label: "At home",
    icon: faChalkboard,
    subtitle: "Online, homeschool",
  },
  {
    value: SchoolingApproach.Hybrid,
    label: "Hybrid",
    icon: faGlobeRegular,
    subtitle: "Campus and home",
  },
];

export const LEARNING_FORMAT_SELECTIONS = [
  {
    value: LearningFormatPreference.Live,
    label: "Live online meetings",
  },
  {
    value: LearningFormatPreference.SelfPaced,
    label: "Self-paced",
  },
];

export const LearningFormatSearchTerms = {
  [LearningFormatPreference.Live]: "Live online",
  [LearningFormatPreference.SelfPaced]: "Self-Paced",
};

/**
 * The learner attribute storing a learner's preferred class size.
 */
export enum ClassSizeLearnerAttribute {
  // @deprecated - use OneOnOne and SmallGroup/LargeGroup instead
  Group = "group",

  OneOnOne = "one_on_one",
  SmallGroup = "small_group",
  LargeGroup = "large_group",
}

export const CLASS_SIZE_SELECTIONS: {
  value: Exclude<ClassSizeLearnerAttribute, "group">;
  label: string;
}[] = [
  {
    value: ClassSizeLearnerAttribute.OneOnOne,
    label: "1-on-1",
  },
  {
    value: ClassSizeLearnerAttribute.SmallGroup,
    label: "Small group (2-8 students)",
  },
  {
    value: ClassSizeLearnerAttribute.LargeGroup,
    label: "Large group (9+ students)",
  },
];

/**
 * Ordered array of class size values for contiguous selection logic
 */
export const CLASS_SIZE_VALUES = CLASS_SIZE_SELECTIONS.map(
  s => s.value,
) as readonly Exclude<ClassSizeLearnerAttribute, "group">[];

// Checks if a value is a valid ClassSizeLearnerAttribute
function isClassSizeLearnerAttribute(
  value: string,
): value is ClassSizeLearnerAttribute {
  return Object.values(ClassSizeLearnerAttribute).includes(
    value as ClassSizeLearnerAttribute,
  );
}

/**
 * Takes in a list of class size attribute strings fetched from learner attributes
 * and returns a list of ClassSizeLearnerAttribute values. This function
 * will filter out any values not present in the ClassSizeLearnerAttribute enum
 * and convers the legacy "group" value to SmallGroup and LargeGroup.
 */
export function parseClassSizeLearnerAttributes(
  classSizes: string[],
): Exclude<ClassSizeLearnerAttribute, "group">[] {
  return uniq(
    classSizes.filter(isClassSizeLearnerAttribute).flatMap(size => {
      if (size === "group") {
        return [
          ClassSizeLearnerAttribute.SmallGroup,
          ClassSizeLearnerAttribute.LargeGroup,
        ];
      }
      return [size];
    }),
  );
}

export type SubjectSelectionType = {
  subject: ActivitySubject;
  icon: IconDefinition;
  order: number;
  preSelect: boolean;
};

export const SUBJECT_SELECTIONS: SubjectSelectionType[] = [
  {
    subject: ActivitySubject.Math,
    icon: faCalculator,
    order: 1,
    preSelect: true,
  },
  {
    subject: ActivitySubject.ScienceAndNature,
    icon: faMicroscope,
    order: 2,
    preSelect: true,
  },
  {
    subject: ActivitySubject.English,
    icon: faBook,
    order: 0,
    preSelect: true,
  },
  {
    subject: ActivitySubject.CodingAndTech,
    icon: faCode,
    order: 5,
    preSelect: false,
  },
  {
    subject: ActivitySubject.WorldLanguages,
    icon: faLanguage,
    order: 4,
    preSelect: false,
  },
  {
    subject: ActivitySubject.Music,
    icon: faMusic,
    order: 8,
    preSelect: false,
  },
  {
    subject: ActivitySubject.TestPrep,
    icon: faFlask,
    order: 11,
    preSelect: false,
  },
  {
    subject: ActivitySubject.Arts,
    icon: faPalette,
    order: 7,
    preSelect: false,
  },
  {
    subject: ActivitySubject.SocialStudies,
    icon: faGlobe,
    order: 3,
    preSelect: true,
  },
  {
    subject: ActivitySubject.EarlyEducation,
    icon: faTeddyBear,
    order: 12,
    preSelect: false,
  },
  {
    subject: ActivitySubject.GamesAndHobbies,
    icon: faPuzzlePiece,
    order: 9,
    preSelect: false,
  },
  {
    subject: ActivitySubject.HealthAndWellness,
    icon: faStaffSnake,
    order: 10,
    preSelect: false,
  },
  {
    subject: ActivitySubject.LifeSkills,
    icon: faLightbulb,
    order: 6,
    preSelect: false,
  },
];

export enum LearningNeedsType {
  ADHD = "adhd",
  ASD = "asd",
  DyslexiaDyscalculiaDysgraphia = "dyslexia_dyscalculia_dysgraphia",
  SpeechLanguageImpairment = "speech_language_impairment",
  Anxiety = "anxiety",
  None = "none",
}

export const LEARNING_NEEDS_SELECTIONS = [
  {
    value: LearningNeedsType.ADHD,
    label: "ADHD",
  },
  {
    value: LearningNeedsType.ASD,
    label: "Autism Spectrum Disorder",
  },
  {
    value: LearningNeedsType.DyslexiaDyscalculiaDysgraphia,
    label: "Dyslexia, Dyscalculia, or Dysgraphia",
  },
  {
    value: LearningNeedsType.SpeechLanguageImpairment,
    label: "Speech or Language Impairment",
  },
  {
    value: LearningNeedsType.Anxiety,
    label: "Anxiety",
  },
  {
    value: LearningNeedsType.None,
    label: "None of the above",
  },
];

export const TIME_OF_DAY_OPTIONS = [...TIMES_OF_DAY];
export const ANYTIME = TIME_OF_DAY_OPTIONS[0];

export const ACTIVITY_PREVIEW_HEIGHT_DESKTOP = "365px";
export const ACTIVITY_PREVIEW_HEIGHT_MOBILE = "318px";
